import {
  ChartArea,
  CircleDollarSign,
  FileText,
  Flag,
  Gauge,
  Gem,
  GitCompareArrows,
  HeartHandshake,
  House,
  LayoutGrid,
  Lightbulb,
  LogOut,
  MousePointerClick,
  Newspaper,
  PencilRuler,
  Plug,
  Search,
  Settings as SettingsLucide,
  ShieldUser,
  ShoppingBasket,
  ShoppingCart,
  Sparkles,
  Store,
  Truck,
  Workflow,
} from "lucide-react";
import React from "react";

import { SlackIcon } from "@src/images/Icons/integrations/SlackIcon";
import type { MainRoute } from "@src/types/navigation";
import { SettingsSectionId } from "@src/types/navigation";
import { isMac } from "@src/utils/deviceDetection";

import {
  ADMIN_ROUTES,
  AI_ROUTES,
  EXPERIENCE_ROUTES,
  GENERAL_ROUTES,
  GLOBAL_STYLES_ROUTES,
  ONBOARDING_ROUTES,
  SETTINGS_ROUTES,
} from "../../routes/routes";

interface AdminTpoSideNavRoute {
  overview: MainRoute;
  billingWorkflowTrace: MainRoute;
  agency: MainRoute;
  agenticLogicStats: MainRoute;
  shippingProviderStats: MainRoute;
  demoStoreManagement: MainRoute;
  regression: MainRoute;
  agencyBilling?: MainRoute;
  slackIntegrations?: MainRoute;
  featureFlags: MainRoute;
  hardLogout: MainRoute;
}

interface SettingsSideNavRoute {
  general: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  cogs: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  currency: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  analytics: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  webhooks: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  customShipping: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  billing: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  agencyBilling?: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  manageFunctions: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  atlasThemeAccess: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  slackSettings: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  pluginConfigurations: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  shopifyConfigurations: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  devTools: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  permissions: Pick<MainRoute, "icon" | "sidebarName" | "path">;
  userPermissions: Pick<MainRoute, "icon" | "sidebarName" | "path">;
}

interface StandardSideNavRoute {
  quickFind: MainRoute;
  getStarted: MainRoute;
  home: MainRoute;

  content: MainRoute;
  merchandising: MainRoute;
  pricing: MainRoute;
  shipping: MainRoute;
  checkout: MainRoute;
  postPurchase: MainRoute;
  sitewideAnalytics: MainRoute;
  opportunityAudit: MainRoute;
  getInspired: MainRoute;
  rules: MainRoute;
  eventsManager: MainRoute;
  widgetLibrary: MainRoute;
  integrations: MainRoute;
  settings: MainRoute;
}

interface BottomSideNavRoute {
  admin: MainRoute;
  ai: MainRoute;
  configCompare: MainRoute;
  performance: MainRoute;
  agencyBilling?: MainRoute;
}

// eslint-disable-next-line react-refresh/only-export-components
export const SettingsRoutes: SettingsSideNavRoute = {
  general: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.General,
    sidebarName: "General",
    icon: null,
  },
  cogs: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Cogs,
    sidebarName: "COGS",
    icon: null,
  },
  currency: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Currency,
    sidebarName: "Currency",
    icon: null,
  },
  analytics: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Analytics,
    sidebarName: "Analytics",
    icon: null,
  },
  webhooks: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Webhooks,
    sidebarName: "Webhooks",
    icon: null,
  },
  customShipping: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.CustomShipping,
    sidebarName: "Custom shipping",
    icon: null,
  },
  billing: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Billing,
    sidebarName: "Billing",
    icon: null,
  },
  agencyBilling: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.AgencyBilling,
    sidebarName: "Brand management",
    icon: null,
  },
  manageFunctions: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.ManageFunctions,
    sidebarName: "Manage functions",
    icon: null,
  },
  atlasThemeAccess: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.AtlasThemeAccess,
    sidebarName: "Atlas theme access",
    icon: null,
  },
  slackSettings: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.SlackSettings,
    sidebarName: "Slack Settings",
    icon: null,
  },
  pluginConfigurations: {
    path:
      SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.PluginConfigurations,
    sidebarName: "Plugin configurations",
    icon: null,
  },
  shopifyConfigurations: {
    path:
      SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.ShopifyConfigurations,
    sidebarName: "Shopify configurations",
    icon: null,
  },
  devTools: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.DevTools,
    sidebarName: "Dev tools",
    icon: null,
  },
  permissions: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.Permissions,
    sidebarName: "Permissions",
    icon: null,
  },
  userPermissions: {
    path: SETTINGS_ROUTES.Settings + "#" + SettingsSectionId.UserPermissions,
    sidebarName: "User permissions",
    icon: null,
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const BillingRoutes: AdminTpoSideNavRoute = {
  overview: {
    path: ADMIN_ROUTES.AdminOrgs,
    sidebarName: "Organizations",
    icon: <Store size={16} />,
  },
  billingWorkflowTrace: {
    path: ADMIN_ROUTES.BillingMetrics,
    sidebarName: "Billing Metrics",
    icon: <Workflow size={16} />,
  },
  agency: {
    path: ADMIN_ROUTES.Agencies,
    sidebarName: "Agencies",
    icon: <HeartHandshake size={16} />,
  },
  agenticLogicStats: {
    path: ADMIN_ROUTES.AgenticLogicStats,
    sidebarName: "Agentic Logic Stats",
    icon: <ChartArea size={16} />,
  },
  shippingProviderStats: {
    path: ADMIN_ROUTES.ShippingProviderStats,
    sidebarName: "Custom Shipping Stats",
    icon: <Truck size={16} />,
  },
  demoStoreManagement: {
    path: ADMIN_ROUTES.DemoStoreManagement,
    sidebarName: "Demo Store Management",
    icon: <Store size={16} />,
  },
  regression: {
    path: ADMIN_ROUTES.Regression,
    sidebarName: "Regression",
    icon: <HeartHandshake size={16} />,
  },
  slackIntegrations: {
    path: ADMIN_ROUTES.SlackIntegrations,
    sidebarName: "Slack Integrations",
    // @ts-ignore
    icon: <SlackIcon fontSize={"16px"} />,
  },
  featureFlags: {
    path: ADMIN_ROUTES.FeatureFlags,
    sidebarName: "Feature Flags",
    icon: <Flag size={16} />,
  },
  hardLogout: {
    path: "#",
    sidebarName: "Hard Logout",
    icon: <LogOut size={16} />,
  },
};

const shortcutKey = isMac() ? "⌘" : "Ctrl";
const shortcutSeparator = isMac() ? "" : " ";

type StandardRoutesParams = { orgId?: string };

export const StandardRoutes = ({
  orgId,
}: StandardRoutesParams = {}): StandardSideNavRoute => ({
  quickFind: {
    path: "#",
    sidebarName: "Quick find",
    icon: <Search size={16} />,
    shortcut: `${shortcutKey}${shortcutSeparator}K`,
    tooltipShortcut: `${shortcutKey}+K`,
  },
  getStarted: {
    path: ONBOARDING_ROUTES.GetStarted,
    sidebarName: "Get Started",
    icon: <Gem size={16} />,
  },
  home: {
    path: GENERAL_ROUTES.Index,
    sidebarName: "Dashboard",
    icon: <House size={16} />,
  },
  content: {
    path: EXPERIENCE_ROUTES.Experiences_Content,
    sidebarName: "Content",
    icon: <PencilRuler size={16} />,
  },
  merchandising: {
    path: EXPERIENCE_ROUTES.Experiences_Merchandising,
    sidebarName: "Merchandising",
    icon: <Newspaper size={16} />,
  },
  pricing: {
    path: EXPERIENCE_ROUTES.Experiences_Pricing,
    sidebarName: "Pricing",
    icon: <CircleDollarSign size={16} />,
  },
  shipping: {
    path: EXPERIENCE_ROUTES.Experiences_Shipping,
    sidebarName: "Shipping",
    icon: <Truck size={16} />,
  },
  checkout: {
    path: EXPERIENCE_ROUTES.Experiences_Checkout,
    sidebarName: "Checkout",
    icon: <ShoppingCart size={16} />,
  },
  postPurchase: {
    path: EXPERIENCE_ROUTES.Experiences_PostPurchaseModule,
    sidebarName: "Post-purchase",
    icon: <ShoppingBasket size={16} />,
  },
  sitewideAnalytics: {
    path: orgId
      ? `${GENERAL_ROUTES.SitewideAnalytics}/${orgId}`
      : GENERAL_ROUTES.SitewideAnalytics,
    sidebarName: "Sitewide Analytics",
    icon: <ChartArea size={16} />,
  },
  opportunityAudit: {
    path: GENERAL_ROUTES.OpportunityAudit,
    sidebarName: "Opportunity Audit",
    icon: <Sparkles size={16} />,
  },
  getInspired: {
    path: GENERAL_ROUTES.GetInspired,
    sidebarName: "Get Inspired",
    icon: <Lightbulb size={16} />,
  },
  rules: {
    path: GENERAL_ROUTES.Rules,
    sidebarName: "Rules",
    icon: <Workflow size={16} />,
  },
  eventsManager: {
    path: GENERAL_ROUTES.EventsManager,
    sidebarName: "Events manager",
    icon: <MousePointerClick size={16} />,
  },
  widgetLibrary: {
    path: GLOBAL_STYLES_ROUTES.GlobalStyles_Experiences,
    sidebarName: "Global styles",
    icon: <LayoutGrid size={16} />,
  },
  integrations: {
    path: GENERAL_ROUTES.Integrations,
    sidebarName: "Integrations",
    icon: <Plug size={16} />,
  },
  settings: {
    path: SETTINGS_ROUTES.Settings,
    sidebarName: "Settings",
    icon: <SettingsLucide size={16} />,
  },
});

export const AdminRoutes = (): BottomSideNavRoute => ({
  admin: {
    path: ADMIN_ROUTES.AdminOrgs,
    sidebarName: "Admin Settings",
    icon: <ShieldUser size={16} />,
  },
  ai: {
    path: AI_ROUTES.Ai,
    sidebarName: "AI",
    icon: <Sparkles size={16} />,
    subRoutes: [
      {
        path: AI_ROUTES.AiTestCategorizations,
        sidebarName: "Test categorizations",
      },
      {
        path: ADMIN_ROUTES.KnowledgeManagement,
        sidebarName: "Knowledge base",
        icon: <FileText size={16} />,
      },
    ],
  },

  configCompare: {
    path: GENERAL_ROUTES.ConfigCompare,
    sidebarName: "Config compare",
    icon: <GitCompareArrows size={16} />,
  },
  performance: {
    path: GENERAL_ROUTES.Performance,
    sidebarName: "Performance",
    icon: <Gauge size={16} />,
  },
});
